'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import SectionTitle from '@/components/ui/SectionTitle';
import StatusBadge from '@/components/ui/StatusBadge';
import ConnectionsKpiRow from './components/ConnectionsKpiRow';
import ConnectionsLineChart from './components/ConnectionsLineChart';
import ConnectionsBarChart from './components/ConnectionsBarChart';
import TierStackedBar from './components/TierStackedBar';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';
import { tierColors, type Tier } from '@/lib/mtf';

export default function ConnectionsDashboardPage() {
  const { profile, allCountries } = useCountry();
  const { t, fmtNum } = useLanguage();
  const [chartView, setChartView] = useState<'rate' | 'tier'>('rate');

  return (
    <AppLayout
      title={t('page.connectionsTitle')}
      subtitle={t('page.connectionsSub')}
    >
      {/* KPI Row */}
      <ConnectionsKpiRow />
      {/* Charts Row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        {/* Line Chart */}
        <div className="bg-surface-card rounded-xl border border-surface-border shadow-card p-5">
          <SectionTitle
            title={t('section.monthlyRollout')}
            subtitle={t('section.monthlyRolloutSub')}
          />
          <ConnectionsLineChart />
        </div>

        {/* Bar / Tier Chart with toggle */}
        <div className="bg-surface-card rounded-xl border border-surface-border shadow-card p-5">
          <SectionTitle
            title={chartView === 'rate' ? t('section.waepAccess') : t('mtf.viewTier')}
            subtitle={chartView === 'rate' ? t('mtf.rateSub') : t('mtf.tierSub')}
            action={
              <div
                className="inline-flex items-center"
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: 2,
                  backgroundColor: 'var(--background)',
                }}
              >
                {(['rate', 'tier'] as const).map(view => (
                  <button
                    key={view}
                    type="button"
                    onClick={() => setChartView(view)}
                    className="text-xs font-semibold transition-colors"
                    style={{
                      padding: '4px 10px',
                      borderRadius: 6,
                      backgroundColor: chartView === view ? 'var(--primary)' : 'transparent',
                      color: chartView === view ? '#ffffff' : 'var(--muted-foreground)',
                      border: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {view === 'rate' ? t('mtf.viewRate') : t('mtf.viewTier')}
                  </button>
                ))}
              </div>
            }
          />
          {chartView === 'rate' ? <ConnectionsBarChart /> : <TierStackedBar />}
        </div>
      </div>
      {/* Country Detail Table */}
      <div className="bg-surface-card rounded-xl border border-surface-border shadow-card mt-6">
        <div className="p-5 border-b border-surface-border">
          <SectionTitle
            title={t('section.countrySummary')}
            subtitle="PEPT, SE4ALL, and national utility program details"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {[
                  t('table.country'),
                  t('table.program'),
                  t('table.utility'),
                  t('table.accessPercent'),
                  t('table.progressToTarget'),
                  t('table.households'),
                  t('table.newThisMonth'),
                  t('table.modalTier'),
                  t('table.status'),
                ]?.map((col) => (
                  <th
                    key={`cc-th-${col}`}
                    className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-text-muted"
                    style={{
                      letterSpacing: '0.06em',
                      backgroundColor: 'var(--background)',
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allCountries?.map((cc, idx) => {
                const isSelected = cc?.country === profile?.name;
                const modalTier = cc?.modalTier as Tier;
                const tierBgColor = tierColors[modalTier];

                // Determine text color based on tier (dark text for light tiers, light for dark)
                const tierTextColor = modalTier >= 3 ? '#0D3B2E' : '#ffffff';

                return (
                  <tr
                    key={cc?.id}
                    className="transition-colors hover:bg-surface-light"
                    style={{
                      borderBottom:
                        idx < allCountries?.length - 1
                          ? '1px solid var(--border)'
                          : 'none',
                      backgroundColor: isSelected ? 'rgba(201,151,58,0.06)' : undefined,
                    }}
                  >
                    <td className="py-3 px-4 font-semibold text-text-main whitespace-nowrap">
                      {cc?.country}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border"
                        style={{
                          backgroundColor: 'rgba(74,158,181,0.1)',
                          color: 'var(--geo)',
                          borderColor: 'rgba(74,158,181,0.2)',
                        }}
                      >
                        {cc?.program}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-text-muted">
                      {cc?.utility}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className="text-base font-bold tabular-nums"
                        style={{
                          fontVariantNumeric: 'tabular-nums',
                          color:
                            cc?.accessPercent >= 80
                              ? 'var(--status-green)'
                              : cc?.accessPercent >= 60
                              ? 'var(--status-amber)'
                              : 'var(--status-red)',
                        }}
                      >
                        {cc?.accessPercent}%
                      </span>
                    </td>
                    <td className="py-3 px-4 min-w-[140px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${cc?.accessPercent}%`,
                              backgroundColor:
                                cc?.accessPercent >= 80
                                  ? 'var(--status-green)'
                                  : cc?.accessPercent >= 60
                                  ? 'var(--status-amber)'
                                  : 'var(--status-red)',
                            }}
                          />
                        </div>
                        <span className="text-[10px] text-text-muted tabular-nums w-8 text-right">
                          {cc?.accessPercent}%
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-semibold tabular-nums text-text-main">
                      {fmtNum(cc?.households ?? 0)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-status-green font-semibold tabular-nums text-sm">
                        +{fmtNum(cc?.newThisMonth ?? 0)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {/* Typical Tier pill */}
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                        style={{
                          backgroundColor: tierBgColor + '26', // 15% opacity ≈ hex 26
                          color: tierBgColor,
                          border: `1px solid ${tierBgColor}40`,
                        }}
                      >
                        {t('tier.' + modalTier)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={cc?.status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          className="px-5 py-3 text-xs text-text-muted"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          Source: USAID Power Africa WAEP Program Data — 5 partner countries tracked
        </div>
      </div>
    </AppLayout>
  );
}